import React from 'react'
import {ContractMethod, ContractTerm} from '../../../../Props/ContractInputInfo'
import {Spacer} from '../../../../../Common/Components/Spacer'
import {PAYMENT} from '../../../../../Common/Constants'
import dayjs from 'dayjs'
import {MBContentBodyTypography, MBContentNoteWithMarkTypography, MBContentSubTitleTypography} from '../../../Atoms/Typographies'
import {MBContentBodyDivider} from '../../../Atoms/Dividers'
import {ContentBlock} from '../../../Molecules/BaseContentBlocks/ContentBlock'
import {ContentTitle} from '../../../Molecules/BaseContentBlocks/ContentTitle'
import {LinkButton} from '../../../Atoms/Buttons'
import {ColumnFlexContainer} from '../../../Atoms/Containers'
import {useTranslation} from 'react-i18next'

type ContractTermConfirmContentProps = {
  title: string
  term: ContractTerm
  contractMethod?: ContractMethod
  nextContractStartDate?: string
}

export const ContractTermConfirmContent: React.VFC<ContractTermConfirmContentProps> = (
  {
    title,
    term,
    contractMethod,
    nextContractStartDate,
  },
) => {
  return (
    <ContentBlock data-testid="block-contract-term-confirm">
      <ContentTitle noMarginBottom={true}>{title}</ContentTitle>
      <Spacer my={'18px'}>
        <ColumnFlexContainer gap={'10px'}>
          <MBContentSubTitleTypography>契約期間</MBContentSubTitleTypography>
          <ContractTermContent
            term={term}
            contractMethod={contractMethod}
            nextContractStartDate={nextContractStartDate}
          />
        </ColumnFlexContainer>
      </Spacer>
      <MBContentBodyDivider/>
    </ContentBlock>
  )
}

type ContractTermContentProps = Omit<ContractTermConfirmContentProps, 'title'>
const ContractTermContent: React.VFC<ContractTermContentProps> = (
  {
    term,
    contractMethod,
    nextContractStartDate,
  },
) => {
  if (nextContractStartDate) {
    return (
      <ColumnFlexContainer gap={'6px'}>
        <ContractTermLabel term={term} contractMethod={contractMethod}/>
        <ContractDurationContent
          startDate={nextContractStartDate}
          endDate={nextContractEndDate(nextContractStartDate, term)}
        />
      </ColumnFlexContainer>
    )
  }

  const now: Date = new Date()
  return <div>
    <ColumnFlexContainer gap={'6px'}>
      <div>
        <ContractTermLabel term={term} contractMethod={contractMethod}/>
        <ContractDurationContent
          startDate={dayjs(now).format('YYYY/MM/DD')}
          endDate={nextContractEndDate(dayjs(now).format('YYYY/MM/DD'), term)}
        />
      </div>
      <ContractTermNoteContent contractMethod={contractMethod}/>
    </ColumnFlexContainer>
  </div>
}

type ContractTermLabelProps = Pick<ContractTermContentProps, 'term' | 'contractMethod'>
const ContractTermLabel: React.VFC<ContractTermLabelProps> = (
  {
    term,
    contractMethod,
  },
) => {
  return (
    <MBContentBodyTypography>
      {`${PAYMENT.TERMS[term].displayLabel}${getSubscriptionLabel(contractMethod)}`}
    </MBContentBodyTypography>
  )
}

const getSubscriptionLabel = (method?: ContractMethod): string => {
  if (!method || method === 'none') {
    return ''
  }
  return method && PAYMENT.SUBSCRIPTION_LABEL[method]
}

type ContractDurationContentProps = {
  startDate: string,
  endDate: string
}
const ContractDurationContent: React.VFC<ContractDurationContentProps> = (
  {
    startDate,
    endDate,
  },
) => {
  return <MBContentBodyTypography>{`${startDate} 〜 ${endDate}`}</MBContentBodyTypography>
}

const nextContractEndDate = (nextContractStartDate: string, term: ContractTerm): string => {
  if (term === 'monthly') {
    return dayjs(nextContractStartDate).endOf('month').format('YYYY/MM/DD')
  }

  return dayjs(nextContractStartDate).add(11, 'month').endOf('month').format('YYYY/MM/DD')
}

type ContractTermNoteContentProps = Pick<ContractTermContentProps, 'contractMethod'>
const ContractTermNoteContent: React.VFC<ContractTermNoteContentProps> = (
  {contractMethod},
) => {
  const {t} = useTranslation('member')

  const cmsDomain: string = process.env.REACT_APP_CMS_DOMAIN ?? ''

  switch (contractMethod) {
    case 'credit_card':
      return (
        <ColumnFlexContainer gap={'4px'} data-testid={'credit-card-note-content'}>
          <MBContentNoteWithMarkTypography
            note={'実際の契約期間は、登録完了画面でご確認ください。'}
          />
          <MBContentNoteWithMarkTypography
            note={
              <>
                <span>{t('新規登録情報確認.契約単位.注記 バケット退会')}に関しては&nbsp;</span>
                <LinkButton
                  href={t('新規登録情報確認.契約単位.注記リンク 有料バケット退会のFAQ', {cmsDomain})}
                  target={'_blank'}
                  fontSize={1.0}
                  lineHeight={1.5}
                >
                  こちら
                </LinkButton>
                <span>&nbsp;をご確認ください。</span>
              </>
            }
          />
        </ColumnFlexContainer>
      )
    case 'mobile_carrier':
      return (
        <ColumnFlexContainer gap={'4px'} data-testid={'mobile-carrier-note-content'}>
          <MBContentNoteWithMarkTypography note={'実際の契約期間は、登録完了画面でご確認ください。'}/>
          <MBContentNoteWithMarkTypography
            note={
              <>
                <span>{t('新規登録情報確認.契約単位.注記 バケット退会')}に関しては&nbsp;</span>
                <LinkButton
                  href={t('新規登録情報確認.契約単位.注記リンク 有料バケット退会のFAQ', {cmsDomain})}
                  target={'_blank'}
                  fontSize={1.0}
                  lineHeight={1.5}
                >
                  こちら
                </LinkButton>
                <span>&nbsp;をご確認ください。</span>
              </>
            }
          />
          <div>
            <MBContentNoteWithMarkTypography
              note={'キャリア決済の場合、サービスメンテナンスのため以下の時間帯に退会および決済方法変更のお手続きできませんのでご注意ください。'}
              subNotes={[
                '月末日19:00 ～ 翌月1日23:59:59 ',
                '入会(キャリア決済へ支払い変更)日当日 〜 翌日1:00',
              ]}
            />
          </div>
        </ColumnFlexContainer>
      )
    case 'convenience_store':
      return (
        <ColumnFlexContainer gap={'4px'} data-testid={'convenience-store-note-content'}>
          <MBContentNoteWithMarkTypography
            note={'上記契約期間は本日中にお支払いの確認が完了した場合で、実際の契約期間はコンビニでお支払いいただいた月から11ヶ月後の月末日までとなります。'}
          />
          <MBContentNoteWithMarkTypography
            note={
              <>
                <span>{t('新規登録情報確認.契約単位.注記 バケット退会')}に関しては&nbsp;</span>
                <LinkButton
                  href={t('新規登録情報確認.契約単位.注記リンク 有料バケット退会のFAQ', {cmsDomain})}
                  target={'_blank'}
                  fontSize={1.0}
                  lineHeight={1.5}
                >
                  こちら
                </LinkButton>
                <span>&nbsp;をご確認ください。</span>
              </>
            }
          />
        </ColumnFlexContainer>
      )
    default:
      return (
        <></>
      )
  }
}
